import dynamic from 'next/dynamic';
import { ComponentProps, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import NoSsrSuspense from '@alltrails/core/components/NoSsrSuspense';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useModal from '@alltrails/modal/hooks/useModal';
import type Context from '@alltrails/types/Context';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type List from '@alltrails/types/List';
import { type Visibility, VISIBILITY_FOLLOWERS_ONLY, VISIBILITY_PRIVATE } from '@alltrails/types/Visibility';
import type WebList from '@alltrails/types/WebList';
import SaveSuccessToast from '../components/SaveSuccessToast';
import { type ListsState, setListsAndListItems, toggleListItem } from '../slices/lists';
import ListItem from '../types/ListItem';
import { getLastSavedList, setLastSavedList } from '../utils/lastSavedListSession';
import { type AnalyticsInfo, logListItemAddedHelper } from '../utils/listAmplitudeHelpers';
import { favoritesListId, isCompleted, isSaved, isVerified as isVerifiedUtil } from '../utils/listUtils';
import { useAppDispatch, useAppSelector } from '../utils/redux';
import getListsAndListItems from '../utils/requests';

const toWebList = (list: List): WebList => ({
  id: list.id,
  order: list.order ?? 0,
  slug: list.slug,
  title: list.title ?? list.name
});

const ReduxSaveToListModal = lazy(() => import('../components/ReduxSaveToListModal'));
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type ItemType = ListItem['type'];

type SignUpInfo = {
  onSuccess?: () => void;
};

/**
 * @param signUpInfo
 * @param preventAutoInitialize boolean to prevent list fetching on successful sign in. NOTE: setting `true` may require you to manually initialize lists.
 * @param analyticsInfo additional analytics info to be passed to the modal
 * @param onSaveToListModalClose called after the save modal closes with all lists the item was added to
 * @returns
 */
const useReduxListItems = (
  signUpInfo?: SignUpInfo,
  preventAutoInitialize?: boolean,
  analyticsInfo?: AnalyticsInfo,
  onSaveToListModalClose?: (lists: WebList[]) => void
) => {
  const dispatch = useAppDispatch();
  const currentUser = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const { listItems, lists } = useAppSelector((state: { lists: ListsState; context: Context }) => ({
    listItems: state.lists.listItems,
    lists: state.lists.lists,
    currentUser: state.context.currentUser
  }));
  const [{ id, type, contentPrivacy, isCustomRoute }, setIdTypeAndPrivacy] = useState<{
    id: number;
    type: ItemType;
    contentPrivacy?: Visibility;
    isCustomRoute?: boolean;
  }>({
    id: 0,
    type: '' as ItemType,
    contentPrivacy: '' as Visibility,
    isCustomRoute: false
  });
  const [shouldOpenListModal, setShouldOpenListModal] = useState(false);
  // The success/removal toast shown after a save action, across every app.
  const [saveToast, setSaveToast] = useState<{ variant: 'saved' | 'removed'; lists: WebList[]; isOpen: boolean }>({
    variant: 'saved',
    lists: [],
    isOpen: false
  });
  // The list a one-tap first save quick-added before opening the modal. The modal seeds its pending
  // saves with this so closing (without further changes) still reports the save and shows the toast.
  const [initialSavedList, setInitialSavedList] = useState<WebList | undefined>(undefined);

  const setToastOpen = useCallback((isOpen: boolean) => setSaveToast(prev => ({ ...prev, isOpen })), []);

  const notifySaved = useCallback(
    (lists: WebList[]) => {
      if (lists.length > 0) {
        setSaveToast({ variant: 'saved', lists, isOpen: true });
      }
      // Consumed — don't reseed it on the next (e.g. "Edit") open of the modal.
      setInitialSavedList(undefined);
      onSaveToListModalClose?.(lists);
    },
    [onSaveToListModalClose]
  );

  const notifyRemoved = useCallback((lists: WebList[]) => {
    if (lists.length > 0) {
      setSaveToast({ variant: 'removed', lists, isOpen: true });
    }
    setInitialSavedList(undefined);
  }, []);

  const { modal, openModal } = useModal(
    ReduxSaveToListModal,
    useMemo(
      () => ({
        id,
        type,
        contentPrivacy,
        analyticsInfo,
        isCustomRoute,
        initialSavedList,
        onItemsRemoved: notifyRemoved,
        onSaveToListModalClose: notifySaved
      }),
      [id, type, contentPrivacy, analyticsInfo, isCustomRoute, initialSavedList, notifyRemoved, notifySaved]
    )
  );

  // Undo a removal from the toast: re-add the item to the list(s) it was removed from.
  const handleUndoRemove = useCallback(() => {
    if (!currentUser) {
      return;
    }
    saveToast.lists.forEach(list => {
      dispatch(toggleListItem({ userId: currentUser.id, listId: list.id, type, objectId: id }));
    });
  }, [currentUser, dispatch, id, saveToast.lists, type]);

  const onSuccess = useCallback(async () => {
    if (preventAutoInitialize) {
      setShouldOpenListModal(true);
    } else {
      const payload = await getListsAndListItems();
      dispatch(setListsAndListItems(payload));
      openModal();
    }

    signUpInfo?.onSuccess?.();
  }, [dispatch, openModal, preventAutoInitialize, signUpInfo]);

  const deeplinkData = useMemo<DeeplinkData | undefined>(() => {
    if (type === 'trail' && id) {
      return { pageId: id, pageType: 'trail' };
    }
    return undefined;
  }, [id, type]);

  const { modal: signUpModal, openModal: openSignupModal } = useModal(
    SignUpModal,
    useMemo<Omit<ComponentProps<typeof SignUpModal>, 'closeModal'>>(
      () => ({
        autoClose: true,
        deeplinkData,
        isOpen: true,
        languageRegionCode,
        onSuccess,
        trigger: 'Favorite',
        upsellTrigger: 'Favorite'
      }),
      [deeplinkData, languageRegionCode, onSuccess]
    )
  );

  const handleFavoriteClick = useCallback(
    (itemType: ItemType, itemId: number, itemPrivacy?: Visibility, isCustomRoute?: boolean) => {
      // Update state before opening the signup modal so that if signup is successful we can automatically open the save to list modal
      setIdTypeAndPrivacy({ id: itemId, type: itemType, contentPrivacy: itemPrivacy, isCustomRoute });

      if (!currentUser) {
        openSignupModal();
        return;
      }

      if (isSaved(listItems, itemType, itemId)) {
        setInitialSavedList(undefined);
        openModal();
        return;
      }

      const lastSavedList = getLastSavedList(currentUser.id);
      const targetList = lastSavedList ? lists.find(list => list.id === lastSavedList.id) : undefined;
      if (targetList) {
        const needsPrivacyCheck = targetList.isCollaborative && (itemPrivacy === VISIBILITY_PRIVATE || itemPrivacy === VISIBILITY_FOLLOWERS_ONLY);
        if (needsPrivacyCheck) {
          setInitialSavedList(undefined);
          openModal();
          return;
        }

        setInitialSavedList(undefined);
        dispatch(toggleListItem({ userId: currentUser.id, listId: targetList.id, type: itemType, objectId: itemId }));
        setLastSavedList(currentUser.id, toWebList(targetList));
        logListItemAddedHelper(targetList.id, itemId, itemType, analyticsInfo?.page);
        notifySaved([toWebList(targetList)]);
        return;
      }

      // First save of the session: auto-save to Favorites and open the modal with it selected.
      // We intentionally do NOT persist the session's last-saved list here — that target is committed
      // only when the modal closes, based on what the item is actually saved to. Otherwise unchecking
      // Favorites (or picking a different list) in the modal would leave a stale target that later
      // one-tap saves would silently reuse.
      const favoritesList = lists.find(list => list.id === favoritesListId);
      if (favoritesList) {
        dispatch(toggleListItem({ userId: currentUser.id, listId: favoritesListId, type: itemType, objectId: itemId }));
        setInitialSavedList(toWebList(favoritesList));
      } else {
        setInitialSavedList(undefined);
      }
      openModal();
    },
    [analyticsInfo?.page, currentUser, dispatch, listItems, lists, notifySaved, openModal, openSignupModal]
  );

  useEffect(() => {
    if (shouldOpenListModal && !!listItems) {
      openModal();
      setShouldOpenListModal(false);
    }
  }, [listItems, openModal, shouldOpenListModal]);

  const isFavorite = useCallback((itemType: string, itemId: number) => isSaved(listItems, itemType, itemId), [listItems]);
  const isComplete = useCallback((itemType: string, itemId: number) => isCompleted(listItems, itemType, itemId), [listItems]);
  const isVerified = useCallback((itemType: string, itemId: number) => isVerifiedUtil(listItems, itemType, itemId), [listItems]);
  return {
    isFavorite,
    isComplete,
    isVerified,
    handleFavoriteClick,
    openListModal: openModal,
    // True only while the "Saved to {list}" toast is showing (not the "Removed from" one), so callers
    // can react to a save (e.g. the trail page's post-save install prompt).
    isSaveToastOpen: saveToast.isOpen && saveToast.variant === 'saved',
    listModal: (
      <>
        <NoSsrSuspense fallback={null}>{modal}</NoSsrSuspense>
        <SaveSuccessToast
          isOpen={saveToast.isOpen}
          lists={saveToast.lists}
          onEdit={openModal}
          onUndo={handleUndoRemove}
          setIsOpen={setToastOpen}
          variant={saveToast.variant}
        />
      </>
    ),
    signUpModal
  };
};

export default useReduxListItems;
